import React, { memo, useEffect, useState } from 'react';
import NextLink from 'next/link';
import { stringToSlug, isJPG } from 'utils';
import { CommerceModel } from 'api-types';
import styles from './BasketLineItem.module.css';
import { generateImageUrl } from '~/shared/utils';
import AddFavoriteButton from '~/components/features/Favorites/AddFavoriteButton/AddFavoriteButton';
import { SvgIcon, InputStepper, Text } from 'ui';
import BaseProductInfo from '~/components/shared/BaseProductInfo/BaseProductInfo';
import { IStatusCodeColor, PriceGroup, StockStatus } from '~/components/features';
import { useTranslations } from '~/services/cms';

export type Props = {
    line: CommerceModel.LineItem;
    onQuantityChange?: (quantity: number) => void;
    isUpdating?: boolean;
};

const isJPGroup = isJPG();

const closestVal = (value: number, colli = 1): number => {
    const leftover = value % colli;
    if (leftover === 0) return value;
    return value + (colli - leftover);
};

const DEBOUNCE_TIME = 1000;
let stateTimeout: NodeJS.Timeout | null = null;

export const BasketLineItem = memo(({ line, onQuantityChange, isUpdating }: Props) => {
    const translate = useTranslations();
    const {
        images,
        name,
        quantity = 1,
        productId,
        priceOutput,
        jpGroupNumber,
        oldJPGroupNumber,
        brand,
        trafficLight,
        stock,
        packingUnit = 1,
        expectedDate
    } = line;

    const colliVal = isJPGroup ? packingUnit : 1;
    const [finalQuantity, setFinalQuantity] = useState(quantity);
    const linkUrl = `/p/${stringToSlug(name || '')}/${productId}/${productId}`;

    const quantityChange = (value: number) => {
        const closestQuantity = closestVal(value, colliVal);
        setFinalQuantity(closestQuantity);
        onQuantityChange && onQuantityChange(closestQuantity);
    };

    const onAmountSearch = (value: number) => {
        if (stateTimeout !== null) {
            clearTimeout(stateTimeout);
        }

        stateTimeout = setTimeout(() => {
            quantityChange(value);
        }, DEBOUNCE_TIME);
    };

    const handleRemove = () => {
        quantityChange(0);
    };

    useEffect(() => {
        if (!isUpdating) {
            setFinalQuantity(quantity);
        }
    }, [isUpdating, quantity]);

    const imageUrl = images && images?.length > 0 ? generateImageUrl({
        size: 'productcarousel',
        fileName: images[0]?.fileName || '',
        modifiedDateTime: images[0]?.modifiedDateTime || ''
    }) : undefined;

    return (
        <div className={styles.productCard}>
            {/* Product Header (image + info) */}
            <div className={styles.productHeader}>
                <NextLink href={linkUrl} passHref>
                    <a href={linkUrl} className={styles.productImageLink}>
                        <BaseProductInfo
                            productName={name}
                            imgSrc={imageUrl}
                            jpGroupNumber={jpGroupNumber}
                            oeNumber={oldJPGroupNumber}
                            brand={brand}
                        />
                    </a>
                </NextLink>
            </div>

            {/* Desktop-only elements */}
            <div className={`${styles.addFavorite} ${styles.desktopOnly}`}>
                <AddFavoriteButton productId={productId || ''} />
            </div>

            <div className={`${styles.statusSection} ${styles.desktopOnly}`}>
                <StockStatus
                    statusCode={trafficLight as IStatusCodeColor}
                    productId={productId || ''}
                    amount={stock}
                    expectedDeliveryDate={expectedDate}
                />
            </div>

            {/* Quantity */}
            <div className={styles.quantitySection}>
                <InputStepper
                    key={`${productId || ''}-${quantity}`}
                    defaultValue={finalQuantity}
                    onChange={(v) => quantityChange(v)}
                    onChangeInput={(v) => onAmountSearch(v)}
                    colli={Number(colliVal)}
                />
            </div>

            {/* Price */}
            <div className={styles.priceSection}>
                <PriceGroup
                    className={undefined}
                    /* Show unit price with packing unit (avoids duplicate per-unit line) */
                    displayPriceInclTax={priceOutput?.salePrice ? priceOutput?.salePrice?.displayTaxedPrice : priceOutput?.unitPrice?.displayTaxedPrice}
                    /* Intentionally omit displayUnitPriceInclTax to prevent duplicate rendering */
                    isOnSale={false}
                    variant="body"
                    orientation="vertical"
                    packingUnit={packingUnit}
                />
                {finalQuantity > 1 ? (
                    <Text as="div" variant="body" className={styles.priceMain}>
                        {priceOutput?.totalPrice?.displayTaxedPrice}
                    </Text>
                ) : null}
            </div>

            {/* Desktop remove icon */}
            <button className={`${styles.removeIcon} ${styles.desktopOnly}`} onClick={handleRemove} aria-label={translate('commerce.basket.removeProductButton')}>
                <SvgIcon name="trash" className={styles.desktopTrashIcon} />
            </button>

            {/* Mobile-only actions */}
            <div className={`${styles.actionsSection} ${styles.mobileOnly}`}>
                <div className={styles.statusLabel}>
                    <StockStatus
                        statusCode={trafficLight as IStatusCodeColor}
                        productId={productId || ''}
                        amount={stock}
                        expectedDeliveryDate={expectedDate}
                    />
                </div>
                <div className={styles.actionButtons}>
                    <div className={styles.favoriteButton}>
                        <AddFavoriteButton productId={productId || ''} />
                    </div>
                    <button className={styles.actionLink} onClick={handleRemove}>
                        <div className={styles.actionIcon}>
                            <SvgIcon name="trash" className={styles.trashIcon} />
                        </div>
                        <Text as="span" variant="bodyExtraSmall" fontWeight="semiBold">
                            {translate('commerce.basket.removeProductButton')}
                        </Text>
                    </button>
                </div>
            </div>
        </div>
    );
});

BasketLineItem.displayName = 'BasketLineItem';

